import {
  Input,
  Button,
  Form,
  FormControl,
  FormLabel,
  FormField,
  FormItem,
  FormMessage,
  FormDescription,
} from '@/src/ui-components';
import { useAuth } from '@/src/contexts';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { cn } from '@/src/helpers';
import { signUpFormSchema, SignUpFormSchema } from '../../shared';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { useEffect } from 'react';

export const SignUpForm = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { signUpWithPassword, loading } = useAuth();

  const form = useForm<SignUpFormSchema>({
    resolver: zodResolver(signUpFormSchema),
    defaultValues: {
      email: '',
      password: '',
      invitation_id: '',
    },
  });

  // Pre-fill invitation code from URL parameter
  useEffect(() => {
    const invitationFromUrl = searchParams.get('invitation');
    if (invitationFromUrl) {
      form.setValue('invitation_id', invitationFromUrl.toLowerCase().trim());
    }
  }, [searchParams, form]);

  const onSubmit = async ({ email, password, invitation_id }: SignUpFormSchema) => {
    const { error } = await signUpWithPassword({
      email,
      password,
      invitation_id,
    });
    if (error) {
      toast.error('Error signing up', {
        description: error,
        duration: 5000,
      });
      return;
    }
    // Successfully signed up and logged in, redirect to trading
    navigate('/trading');
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="w-full space-y-6">
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Email Address</FormLabel>
              <FormControl>
                <Input {...field} placeholder="Enter your email" autoComplete="email" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="invitation_id"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Invitation Code</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  placeholder="Enter your 32-character code"
                  maxLength={32}
                  autoComplete="off"
                  onChange={(e) => {
                    // Auto-format: remove spaces and convert to lowercase
                    const formatted = e.target.value.replace(/\s/g, '').toLowerCase();
                    field.onChange(formatted);
                  }}
                />
              </FormControl>
              <FormDescription className="text-xs">
                Enter the invitation code you received via email
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="password"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Password</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  type="password"
                  placeholder="Create a strong password"
                  autoComplete="new-password"
                />
              </FormControl>
              <FormDescription className="text-xs">
                Must be at least 8 characters
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <Button
          type="submit"
          disabled={loading}
          className={cn(
            'h-12 w-full transform rounded-lg bg-gradient-to-r from-primary to-primary/80 font-medium text-primary-foreground transition-all duration-200 hover:scale-[1.02] hover:from-primary/90 hover:to-primary/70 disabled:transform-none',
            loading && 'opacity-50'
          )}
        >
          {loading ? 'Creating Account' : 'Create Account'}
        </Button>
      </form>
    </Form>
  );
};
